import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Input, 
  Button, 
  Space, 
  Typography, 
  Row,
  Col,
  Avatar,
  Tooltip,
  Modal,
  Progress,
  Spin,
  message,
  App,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import SearchCard from './SearchCard';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import questionnaireService from '../services/questionnaireService';

const { Title, Text } = Typography;

const cardStyles = `
  .questionnaire-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    transition: all 0.3s ease;
    cursor: pointer;
    height: 220px !important;
    display: flex !important;
    flex-direction: column !important;
  }
  
  .questionnaire-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    transform: translateY(-2px);
  }
  
  .questionnaire-card .ant-card-body {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 16px !important;
  }
  
  .questionnaire-card-title {
    height: 24px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px !important;
  }
  
  .questionnaire-card-description {
    height: 40px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 4px;
  }
  
  .questionnaire-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  
  .action-button {
    position: absolute;
    top: 12px;
    right: 12px;
    color: #2E86AB;
  }
`;

const QuestionnaireList = () => {
  const { modal, message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  // Fetch questionnaires from API
  const fetchQuestionnaires = async () => {
    try {
      setLoading(true);
      const response = await questionnaireService.getAll({ fetchAll: true, limit: 100 });
      if (response.success) {
        setQuestionnaires(response.data || []);
      } else {
        console.error('Failed to fetch quizzes:', response.message);
        setQuestionnaires([]);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      setQuestionnaires([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const handleCreateNew = () => {
    navigate('/dashboard/questionnaires/new');
  };

  const handleEdit = (id) => {
    navigate(`/dashboard/questionnaires/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/dashboard/questionnaires/edit/${id}`);
  };

  const handlePreview = (questionnaire) => {
    // Open preview in new tab without dashboard layout
    window.open(`/preview/${questionnaire._id}`, '_blank');
  };

  const handleDelete = async (id) => {
    modal.confirm({
      title: 'Delete Quiz',
      content: 'Are you sure you want to delete this quiz? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          const response = await questionnaireService.delete(id);
          if (response.success) {
            setQuestionnaires(prev => prev.filter(q => q._id !== id));
            messageApi.success('Quiz deleted successfully');
          } else {
            modal.error({
              title: 'Delete Failed',
              content: response.message || 'Failed to delete quiz'
            });
          }
        } catch (error) {
          console.error('Error deleting quiz:', error);
          modal.error({
            title: 'Delete Failed',
            content: 'An error occurred while deleting the quiz'
          });
        }
      }
    });
  };

  const handleCompleteQuestionnaire = async (questionnaireId) => {
    try {
      const response = await questionnaireService.complete(questionnaireId);
      if (response.success) {
        messageApi.success('Quiz completed successfully');
        // Refresh the questionnaire list
        await fetchQuestionnaires();
      } else {
        messageApi.error(response.message || 'Failed to complete quiz');
      }
    } catch (error) {
      console.error('Error completing quiz:', error);
      messageApi.error('Failed to complete quiz');
    }
  };

  const handleArchiveQuestionnaire = async (questionnaireId) => {
    try {
      const response = await questionnaireService.archive(questionnaireId);
      if (response.success) {
        messageApi.success('Quiz archived successfully');
        // Refresh the questionnaire list
        await fetchQuestionnaires();
      } else {
        messageApi.error(response.message || 'Failed to archive quiz');
      }
    } catch (error) {
      console.error('Error archiving quiz:', error);
      messageApi.error('Failed to archive quiz');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#faad14';
      case 'complete': return '#52c41a';
      case 'archived': return '#8c8c8c';
      default: return '#faad14';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <EditOutlined />;
      case 'complete': return <CheckCircleOutlined />;
      case 'archived': return <InboxOutlined />;
      default: return <EditOutlined />;
    }
  };

  const getStatusColors = (status) => {
    switch (status) {
      case 'complete':
        return {
          borderColor: '#52c41a',
          backgroundColor: '#f6ffed',
          shadowColor: 'rgba(82, 196, 26, 0.15)'
        };
      case 'draft':
        return {
          borderColor: '#faad14',
          backgroundColor: '#fffbe6',
          shadowColor: 'rgba(250, 173, 20, 0.15)'
        };
      case 'archived':
        return {
          borderColor: '#8c8c8c',
          backgroundColor: '#f5f5f5',
          shadowColor: 'rgba(140, 140, 140, 0.15)'
        };
      default:
        return {
          borderColor: '#d9d9d9',
          backgroundColor: '#fafafa',
          shadowColor: 'rgba(0, 0, 0, 0.06)'
        };
    }
  };

  const getStatusTagColor = (status) => {
    switch (status) {
      case 'complete': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const filteredQuestionnaires = questionnaires.filter(questionnaire =>
    questionnaire.title?.toLowerCase().includes(searchText.toLowerCase()) ||
    questionnaire.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const renderQuestionnaireCard = (questionnaire) => {
    const statusColors = getStatusColors(questionnaire.status);
    
    return (
      <Card
        key={questionnaire._id}
        className="questionnaire-card"
        onClick={() => handleView(questionnaire._id)}
        style={{
          border: '1px solid #f0f0f0',
          borderTop: `4px solid ${statusColors.borderColor}`,
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          height: '220px',
          display: 'flex',
          flexDirection: 'column'
        }}
        bodyStyle={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          padding: '16px' 
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
        }}
      >
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <Space size="small">
            {questionnaire.status === 'draft' ? (
              <Tooltip title="Complete">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCompleteQuestionnaire(questionnaire._id);
                  }}
                  style={{ padding: '0 4px', fontSize: '12px', color: '#52c41a' }}
                />
              </Tooltip>
            ) : null}
            
            {questionnaire.status === 'complete' ? (
              <Tooltip title="Archive">
                <Button
                  type="text"
                  size="small"
                  icon={<InboxOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchiveQuestionnaire(questionnaire._id);
                  }}
                  style={{ padding: '0 4px', fontSize: '12px', color: '#8c8c8c' }}
                />
              </Tooltip>
            ) : null}
            
            <Tooltip title="Preview">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview(questionnaire);
                }}
                style={{ padding: '0 4px', fontSize: '12px', color: '#2E86AB' }}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(questionnaire._id);
                }}
                style={{ padding: '0 4px', fontSize: '12px', color: '#2E86AB' }}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(questionnaire._id);
                }}
                style={{ padding: '0 4px', fontSize: '12px', color: '#ff4d4f' }}
              />
            </Tooltip>
          </Space>
        </div>

        <div className="questionnaire-card-content">
          {/* Title and Description Section - Fixed Height */}
          <div style={{ marginBottom: '8px', marginTop: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <Title level={5} className="questionnaire-card-title" style={{ 
                color: '#2E86AB', 
                marginTop: "8px",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                marginRight: '8px',
                lineHeight: '1.2'
              }}>
                {questionnaire.title}
              </Title>
     
            </div>
            <div className="questionnaire-card-description" style={{ fontSize: '14px', color: '#666'}}>
              {questionnaire?.description || 'No description'}
            </div>
          </div>

          {/* Questions and Duration Section - Fixed Height */}
          <div style={{ marginBottom: '4px', height: '20px' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <Space size="large">
                <Space size="small">
                  <QuestionCircleOutlined style={{ color: '#2E86AB' }} />
                  <span>
                    {(() => {
                      const count = questionnaire?.questions?.length ?? questionnaire?.questionCount ?? 0;
                      return `${count} question${count !== 1 ? 's' : ''}`;
                    })()}
                  </span>
                </Space>
                
                {questionnaire?.estimatedDuration && (
                  <Space size="small">
                    <ClockCircleOutlined style={{ color: '#2E86AB' }} />
                    <span>{questionnaire.estimatedDuration} min</span>
                  </Space>
                )}
              </Space>
            </div>
          </div>

          {/* Footer Section - Fixed Height at Bottom */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '20px', marginTop: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
              <UserOutlined style={{ color: '#666', fontSize: '12px' }} />
              <span style={{ 
                fontSize: '12px', 
                color: '#666',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {questionnaire.createdBy?.name ? 
                  `${questionnaire.createdBy.name} ${questionnaire.createdBy.surname || ''}`.trim() :
                  'Unknown User'
                }
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#999', marginLeft: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>
                {questionnaire.updatedAt ? 
                  `Updated ${new Date(questionnaire.updatedAt).toLocaleDateString()}` :
                  `Created ${new Date(questionnaire.createdAt).toLocaleDateString()}`
                }
              </span>
              <span style={{ 
                fontSize: '11px',
                color: getStatusColor(questionnaire.status),
                fontWeight: '500',
                textTransform: 'capitalize'
              }}>
                • {questionnaire.status}
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <style>{cardStyles}</style>
      
      <PageHeader 
        title="Quizzes"
        breadcrumb={BREADCRUMB_PATTERNS.questionnaires}
        rightContent={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateNew}
          >
            Create New Quiz
          </Button>
        }
      />

      <SearchCard
        searchPlaceholder="Search quizzes by title or description..."
        searchValue={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        onSearchClick={() => {/* Search is handled by filtering */}}
        theme="white"
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading quizzes...</Text>
          </div>
        </div>
      ) : filteredQuestionnaires.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#999', fontSize: '48px', marginBottom: '16px' }}>
            <QuestionCircleOutlined />
          </div>
          <Title level={4} style={{ color: '#999' }}>
            {searchText ? 'No quizzes found' : 'No quizzes available'}
          </Title>
          <Text type="secondary">
            {searchText 
              ? 'Try adjusting your search criteria' 
              : 'Create your first quiz using the button above'
            }
          </Text>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredQuestionnaires.map((questionnaire) => (
            <Col xs={24} sm={12} md={8} lg={6} key={questionnaire._id}>
              {renderQuestionnaireCard(questionnaire)}
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default QuestionnaireList;